import { Card, Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type GetModuleSpecsQuery } from '../../../../api/queries/getModuleSpecs';
import { InsurerIntegrationProvider, ModuleRole } from '../../../../api/types';
import DealershipPriceDisclaimerField from '../../../../components/fields/DealershipFields/DealershipPriceDisclaimer';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import ModuleSelectField from '../../../../components/fields/ModuleSelectField';
import SwitchField from '../../../../components/fields/SwitchField';
import TranslatedInputField from '../../../../components/fields/TranslatedInputField';
import useMarkDownInfoTooltip from '../../../../utilities/useMarkDownInfoTooltip';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import { type NewInsurerFormValues } from './CreateInsurerFrom';

const colSpan = { lg: 8, md: 12, xs: 24 };

export type InsurerDetailsFormProps = {
    module: GetModuleSpecsQuery['module'];
};

const CreateInsurerDetailsForm = ({ module }: InsurerDetailsFormProps) => {
    const { t } = useTranslation(['insurerDetails']);
    const { yesNoSwitch } = useSystemSwitchData();
    const markdownInfoTooltip = useMarkDownInfoTooltip();

    const { values, setFieldValue } = useFormikContext<NewInsurerFormValues>();
    useEffect(() => {
        if (!values.sendEmailToCustomer) {
            setFieldValue('sendPDFToCustomer', false);
        }
    }, [setFieldValue, values.sendEmailToCustomer]);

    const showCommentsField = useMemo(
        () =>
            [InsurerIntegrationProvider.Email, InsurerIntegrationProvider.Eazy].some(
                integration => integration === values?.integrationProvider
            ),
        [values?.integrationProvider]
    );

    return (
        <Card title={t('insurerDetails:cardTitles.insurerDetails')}>
            <Row gutter={14}>
                <Col {...colSpan}>
                    <InputField
                        {...t('insurerDetails:fields.name', { returnObjects: true })}
                        name="displayName"
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <TranslatedInputField
                        {...t('insurerDetails:fields.legalName', { returnObjects: true })}
                        name="legalName"
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <ModuleSelectField
                        {...t('insurerDetails:fields.module', { returnObjects: true })}
                        moduleRole={ModuleRole.Insurance}
                        name="moduleId"
                        required
                    />
                </Col>

                <Col {...colSpan}>
                    <InputField {...t('insurerDetails:fields.email', { returnObjects: true })} name="email" required />
                </Col>
                <Col {...colSpan}>
                    <InputNumberField
                        {...t('insurerDetails:fields.orderNo', { returnObjects: true })}
                        name="order"
                        required
                    />
                </Col>
                <Col lg={8} xs={24}>
                    <DealershipPriceDisclaimerField
                        {...t('insurerDetails:fields.insuranceDisclaimer', { returnObjects: true })}
                        disclaimerKind="insuranceDisclaimer"
                        name="insuranceDisclaimer"
                        tooltip={markdownInfoTooltip}
                        isMultipleEntry
                    />
                </Col>
                <Col {...colSpan}>
                    <SwitchField
                        {...t('insurerDetails:fields.sendEmailToCustomer', { returnObjects: true })}
                        name="sendEmailToCustomer"
                        {...yesNoSwitch}
                        required
                    />
                </Col>
                {values.sendEmailToCustomer && (
                    <Col {...colSpan}>
                        <SwitchField
                            {...t('insurerDetails:fields.sendPDFToCustomer', { returnObjects: true })}
                            name="sendPDFToCustomer"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                )}

                <Col {...colSpan}>
                    <ModuleSelectField
                        {...t('insurerDetails:fields.submission', { returnObjects: true })}
                        companyId={module?.companyId}
                        moduleRole={ModuleRole.Signing}
                        name="submissionApprovalModuleId"
                        addNoneOption
                    />
                </Col>
                <Col {...colSpan}>
                    <ModuleSelectField
                        {...t('insurerDetails:fields.resubmission', { returnObjects: true })}
                        companyId={module?.companyId}
                        moduleRole={ModuleRole.Signing}
                        name="reSubmissionApprovalModuleId"
                        addNoneOption
                    />
                </Col>

                {showCommentsField && (
                    <Col {...colSpan}>
                        <SwitchField
                            {...t('insurerDetails:fields.showCommentsField', { returnObjects: true })}
                            name="showCommentsField"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                )}

                <Col {...colSpan}>
                    <SwitchField
                        {...t('insurerDetails:fields.active', { returnObjects: true })}
                        name="isActive"
                        {...yesNoSwitch}
                        required
                    />
                </Col>
            </Row>
        </Card>
    );
};

export default CreateInsurerDetailsForm;
